import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface AttemptAnalyticsProps {
  courseId: string;
}

const AttemptAnalytics: React.FC<AttemptAnalyticsProps> = ({ courseId }) => {
  const [dateRange, setDateRange] = useState('monthly');

  // Mock data
  const improvementTrends = [
    { attempt: 1, avgScore: 65, passRate: 45 },
    { attempt: 2, avgScore: 75, passRate: 68 },
    { attempt: 3, avgScore: 82, passRate: 85 },
    { attempt: 4, avgScore: 88, passRate: 92 },
    { attempt: 5, avgScore: 91, passRate: 96 },
  ];

  const attemptsPerTopic = [
    { topic: 'Linear Equations', avgAttempts: 1.8, maxAttempts: 4 },
    { topic: 'Quadratic Functions', avgAttempts: 2.1, maxAttempts: 5 },
    { topic: 'Polynomials', avgAttempts: 2.5, maxAttempts: 6 },
    { topic: 'Trigonometry', avgAttempts: 3.2, maxAttempts: 8 },
    { topic: 'Calculus Basics', avgAttempts: 3.5, maxAttempts: 9 },
  ];

  const attemptDistribution = [
    { attempts: 1, count: 145 },
    { attempts: 2, count: 68 },
    { attempts: 3, count: 25 },
    { attempts: 4, count: 12 },
    { attempts: 5, count: 5 },
  ];

  const timeBetweenAttempts = [
    { range: '0-6 hours', count: 45 },
    { range: '6-12 hours', count: 68 },
    { range: '12-24 hours', count: 95 },
    { range: '1-3 days', count: 42 },
    { range: '3+ days', count: 15 },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Attempt Analysis
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Date Range</InputLabel>
          <Select
            value={dateRange}
            label="Date Range"
            onChange={(e) => setDateRange(e.target.value)}
          >
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="all">All Time</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {/* Overview Cards */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Attempts
              </Typography>
              <Typography variant="h4">1,245</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Avg Attempts/Student
              </Typography>
              <Typography variant="h4">5.1</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                First Attempt Pass
              </Typography>
              <Typography variant="h4">65%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Avg Time Between
              </Typography>
              <Typography variant="h4">18 hrs</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Improvement Trends */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Improvement Trends Across Attempts
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={improvementTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="attempt" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="avgScore" stroke="#8884d8" strokeWidth={2} name="Average Score %" />
                <Line type="monotone" dataKey="passRate" stroke="#82ca9d" strokeWidth={2} name="Pass Rate %" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Attempt Distribution */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Attempt Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={attemptDistribution} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="attempts" type="category" />
                <Tooltip />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Attempts per Topic */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Average Attempts per Topic
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={attemptsPerTopic}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="topic" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="avgAttempts" fill="#8884d8" name="Average Attempts" />
                <Bar dataKey="maxAttempts" fill="#ff6b6b" name="Max Attempts" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Time Between Attempts */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Time Between Attempts
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={timeBetweenAttempts}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Average: <strong>18 hours</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Median: <strong>15 hours</strong>
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Attempts per Topic Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Attempt Statistics by Topic
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Topic</TableCell>
                    <TableCell align="right">Avg Attempts</TableCell>
                    <TableCell align="right">Max Attempts</TableCell>
                    <TableCell align="right">First Attempt Pass</TableCell>
                    <TableCell align="right">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attemptsPerTopic.map((topic, index) => {
                    const firstAttemptPass = Math.round(100 - (topic.avgAttempts - 1) * 25);
                    return (
                      <TableRow key={index}>
                        <TableCell>{topic.topic}</TableCell>
                        <TableCell align="right">{topic.avgAttempts}</TableCell>
                        <TableCell align="right">{topic.maxAttempts}</TableCell>
                        <TableCell align="right">{firstAttemptPass}%</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={topic.avgAttempts <= 2 ? 'Good' : topic.avgAttempts <= 3 ? 'Fair' : 'Needs Improvement'}
                            color={topic.avgAttempts <= 2 ? 'success' : topic.avgAttempts <= 3 ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AttemptAnalytics;

